import { createFileRoute, Link } from '@tanstack/react-router';
import { LegalPageLayout } from '@/components/legal/LegalPageLayout';

export const Route = createFileRoute('/legal/cookies')({
  component: CookiePolicy,
});

function CookiePolicy() {
  return (
    <LegalPageLayout title="Cookie Policy" lastUpdated="February 25, 2026">
      <p>
        This Cookie Policy explains how Wundra, operated by Grateful8 Consulting
        LLC, uses cookies and similar storage technologies when you use our
        Service.
      </p>

      <h2>1. What Are Cookies?</h2>
      <p>
        Cookies are small text files stored on your device by your web browser.
        We also use browser local storage, which serves a similar purpose.
        Together, these technologies help us provide essential functionality,
        remember your preferences, and understand how you use Wundra.
      </p>

      <h2>2. Essential Storage</h2>
      <p>
        These are required for the Service to function and cannot be disabled:
      </p>
      <ul>
        <li>
          <strong>Theme preference</strong> (
          <code>wundra-theme</code>) — Stores your light/dark mode selection in
          local storage
        </li>
        <li>
          <strong>Authentication tokens</strong> — Firebase Authentication
          session cookies that keep you signed in
        </li>
        <li>
          <strong>Analytics consent</strong> (
          <code>analytics_consent</code>) — Records whether you have opted in or
          out of analytics tracking
        </li>
      </ul>

      <h2>3. Analytics Cookies</h2>
      <p>
        We use Firebase Analytics to understand how users interact with Wundra.
        Analytics tracking is <strong>opt-in only</strong> and is not enabled
        until you explicitly consent.
      </p>
      <ul>
        <li>
          Your consent preference is stored in local storage under the{' '}
          <code>analytics_consent</code> key
        </li>
        <li>
          When enabled, Firebase Analytics may set cookies to track session
          information, page views, and feature usage
        </li>
        <li>
          You can change your analytics preference at any time in your profile
          settings
        </li>
      </ul>

      <h2>4. Affiliate &amp; Referral Tracking</h2>
      <p>
        When you arrive at Wundra via an affiliate or referral link, we store
        attribution data in local storage:
      </p>
      <ul>
        <li>
          <strong>Affiliate tracking</strong> (
          <code>affiliate_tracking</code>) — Stores the affiliate code, UTM
          parameters, and referral source
        </li>
        <li>This data has a 60-day expiry</li>
        <li>
          It is used solely to credit the referring affiliate partner if you make
          a purchase
        </li>
      </ul>

      <h2>5. Third-Party Cookies</h2>
      <p>
        Some third-party services integrated with Wundra may set their own
        cookies:
      </p>
      <ul>
        <li>
          <strong>Google</strong> — Google Maps and Google Places may set cookies
          for map functionality and usage analytics
        </li>
        <li>
          <strong>Stripe</strong> — May set cookies during the payment process
          for fraud prevention and session management
        </li>
        <li>
          <strong>Firebase</strong> — May set cookies for authentication and
          analytics (when opted in)
        </li>
      </ul>
      <p>
        These cookies are governed by the respective third-party privacy
        policies.
      </p>

      <h2>6. Managing Your Preferences</h2>
      <p>You can manage cookie and storage preferences in several ways:</p>
      <ul>
        <li>
          <strong>Analytics opt-out:</strong> Toggle analytics tracking off in
          your{' '}
          <Link to="/profile" search={{ tab: 'profile' }}>
            profile settings
          </Link>
        </li>
        <li>
          <strong>Browser settings:</strong> Most browsers allow you to block or
          delete cookies through their settings menu
        </li>
        <li>
          <strong>Local storage:</strong> You can clear local storage through
          your browser's developer tools
        </li>
      </ul>
      <p>
        Note that disabling essential cookies or clearing authentication storage
        may prevent you from using certain features of Wundra.
      </p>

      <h2>7. Changes to This Policy</h2>
      <p>
        We may update this Cookie Policy as we add new features or integrate new
        services. Changes will be reflected on this page with an updated "Last
        updated" date.
      </p>

      <h2>8. Contact Us</h2>
      <p>
        If you have questions about our use of cookies, contact us at:
      </p>
      <ul>
        <li>Grateful8 Consulting LLC</li>
        <li>
          Email: <a href="mailto:hello@wundra.app">hello@wundra.app</a>
        </li>
      </ul>
    </LegalPageLayout>
  );
}
